import { priceFormatWithZerosReduction } from "@gameApp/helpers/price/priceFormatWithZerosReduction";
import { priceGetCurrencySymbol } from "@gameApp/helpers/price/priceGetCurrencySymbol";
import { priceGetDivisorName } from "@gameApp/helpers/price/priceGetDivisorName";
import { pricePrepare } from "@gameApp/helpers/price/pricePrepare";
import type { DivisorNames } from "@gameApp/helpers/price/types";

const MIN_PRICE_FOR_REDUCTION = 0.000001;

export interface PriceFormatParams {
  price?: number | string;
  currency?: string;
  fallback?: string;
  options?: Intl.NumberFormatOptions;
  withoutReduction?: boolean;
  excludedDivisors?: DivisorNames[];
  rounding?: "floor" | "ceil" | "round";
}

/**
 * * Rounds a number, adds a currency symbol and an optional divisor suffix (К, М, B, T)
 * */
export function priceFormat(params: PriceFormatParams): string {
  const {
    price,
    currency = "",
    options,
    fallback = "--",
    withoutReduction = false,
    excludedDivisors = ["K"],
    rounding,
  } = params;

  if (price === undefined || price === null) {
    return fallback;
  }

  let numericPrice: number;

  if (typeof price === "string") {
    numericPrice = Number.parseFloat(price);
  } else {
    numericPrice = price;
  }

  if (!Number.isFinite(numericPrice)) return fallback;

  // Positive price for calculations
  const absPrice = Math.abs(numericPrice);
  // Output consists of sign, currency symbol, divisor, price
  // keep the price sign, then work with a positive number
  const sign = numericPrice >= 0 ? "" : "-";
  // Get currency symbol if present ($, £, ₽)
  const currencySymbol = priceGetCurrencySymbol(currency);

  // Get the divisor (K, M, B, T)
  const divisorName = priceGetDivisorName({
    price: absPrice,
    excludedDivisors,
  });

  // Get the price (separated if there is a divisor) with grouping thousandths (1,000,000)
  const stringPrice = pricePrepare({
    price: absPrice,
    divisorName,
    options,
    rounding,
  });

  const divisorWithSpace = divisorName ? `\u00a0${divisorName}` : "";
  const currencyWithSpace = `\u00a0${currency}`;

  // -100 B RUB
  let formattedPrice =
    sign + stringPrice + divisorWithSpace + currencyWithSpace;

  // -100 B
  if (!currency) {
    formattedPrice = sign + stringPrice + divisorWithSpace;
  }

  // -$100 B
  if (currencySymbol) {
    formattedPrice = sign + currencySymbol + stringPrice + divisorWithSpace;
  }

  if (withoutReduction || absPrice >= MIN_PRICE_FOR_REDUCTION) {
    return formattedPrice;
  }

  return priceFormatWithZerosReduction({
    rawPrice: numericPrice,
    formattedPrice,
    currency,
  });
}
